/**
 * The plugin frame's policy, as the renderer loads a bundle.
 *
 * A bundle carries its Content-Security-Policy in a `<meta>` tag the build
 * wrote (`services/plugin-build-service`, `@fias/csp`), and the renderer fills
 * its placeholders as it loads it (`PluginRenderer`). One origin is ADDED here
 * rather than built in: the platform's data-storage CDN (`VITE_DATA_STORAGE_CDN_URL`,
 * the DataStorageStack's CloudFront distribution), in `img-src` only. The bridge
 * hands plugins `dfe-icons/` and `dfe-avatars/` pictures as that CDN's URLs
 * (`bridge-asset-presigner.ts`): one stable URL per picture, which a browser
 * caches, where a presigned S3 URL changed on every read and the picture was
 * downloaded again on every page load (the owner, 2026-10-18). Adding it as the
 * bundle loads covers every bundle ever built — none carries a placeholder for
 * it, and a rebuild of every published plugin is not a precondition for a
 * caching fix. It widens `img-src` by one Fias-owned origin whose objects are
 * already public; nothing else in the policy changes, and a bundle whose policy
 * has no `img-src` directive is left as it is (its `default-src` stands).
 */

/** The data-storage CDN's origin, or '' where the environment has none (local dev). */
export function dataStorageImageOrigin(
  base: string | undefined = import.meta.env.VITE_DATA_STORAGE_CDN_URL as string | undefined,
): string {
  if (!base) return '';
  try {
    const url = new URL(base);
    return url.protocol === 'https:' ? url.origin : '';
  } catch {
    return '';
  }
}

const CSP_META = /(<meta\s+http-equiv=(["'])Content-Security-Policy\2\s+content=")([^"]*)(")/i;

/** `html` with `origin` added to the `img-src` of its first Content-Security-Policy meta tag. */
export function withImageOrigin(html: string, origin: string): string {
  if (!origin) return html;
  return html.replace(CSP_META, (_all, open: string, _q: string, policy: string, close: string) => {
    const directives = policy.split(';').map((d) => {
      const words = d.trim().split(/\s+/);
      if (words[0]?.toLowerCase() !== 'img-src' || words.includes(origin)) return d;
      return `${d.replace(/\s+$/, '')} ${origin}`;
    });
    return open + directives.join(';') + close;
  });
}

/** The renderer's one call: the bundle with the data-storage CDN allowed for its pictures. */
export function withDataStorageImageOrigin(html: string): string {
  return withImageOrigin(html, dataStorageImageOrigin());
}
